import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  Company,
  CompanyDuplicateCandidate,
  CompanyDuplicateCandidatesParams,
  CompanyRequest,
  CompanyResponse,
  CompanySearchParams,
  PageResponse
} from '../models/empresa.model';
import { ImportMode, ImportResultResponse } from '../../../shared/models/import-export.model';
import { FullHttpParameterCodec } from '../../../shared/http/full-parameter-codec';

@Injectable({
  providedIn: 'root'
})
export class EmpresaApiService {
  private readonly apiUrl = `${environment.apiUrl}api`;

  constructor(private http: HttpClient) { }

  // GET /api/companies - List companies with filters and pagination
  searchCompanies(params: CompanySearchParams): Observable<PageResponse<CompanyResponse>> {
    let httpParams = new HttpParams({ encoder: new FullHttpParameterCodec() })
      .set('businessId', params.businessId.toString());

    if (params.phonePrefix) {
      httpParams = httpParams.set('phonePrefix', params.phonePrefix);
    }

    if (params.idCompanyRelationshipType !== undefined) {
      httpParams = httpParams.set('idCompanyRelationshipType', params.idCompanyRelationshipType.toString());
    }
    if (params.idCompanySize !== undefined) {
      httpParams = httpParams.set('idCompanySize', params.idCompanySize.toString());
    }
    if (params.idCompanySector !== undefined) {
      httpParams = httpParams.set('idCompanySector', params.idCompanySector.toString());
    }
    if (params.idCompanyStateLead !== undefined) {
      httpParams = httpParams.set('idCompanyStateLead', params.idCompanyStateLead.toString());
    }
    if (params.idCompanyCycle !== undefined) {
      httpParams = httpParams.set('idCompanyCycle', params.idCompanyCycle.toString());
    }
    if (params.searchTerm) {
      httpParams = httpParams.set('searchTerm', params.searchTerm);
    }
    if (params.contactSearchTerm) {
      httpParams = httpParams.set('contactSearchTerm', params.contactSearchTerm);
    }
    if (params.userPropietyId !== undefined) {
      httpParams = httpParams.set('userPropietyId', params.userPropietyId.toString());
    }
    if (params.page !== undefined) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params.size !== undefined) {
      httpParams = httpParams.set('size', params.size.toString());
    }

    return this.http.get<PageResponse<CompanyResponse>>(`${this.apiUrl}/companies`, { params: httpParams });
  }

  // GET /api/companies/duplicate-candidates - Candidatos a duplicado ANTES de guardar (advertencia preventiva, nunca bloquea)
  getDuplicateCandidates(params: CompanyDuplicateCandidatesParams): Observable<CompanyDuplicateCandidate[]> {
    let httpParams = new HttpParams()
      .set('businessId', params.businessId.toString());

    if (params.commercialName !== undefined) {
      httpParams = httpParams.set('commercialName', params.commercialName);
    }
    if (params.businessName !== undefined) {
      httpParams = httpParams.set('businessName', params.businessName);
    }
    if (params.website !== undefined) {
      httpParams = httpParams.set('website', params.website);
    }
    if (params.taxCode !== undefined) {
      httpParams = httpParams.set('taxCode', params.taxCode);
    }
    if (params.excludeId !== undefined) {
      httpParams = httpParams.set('excludeId', params.excludeId.toString());
    }

    return this.http.get<CompanyDuplicateCandidate[]>(`${this.apiUrl}/companies/duplicate-candidates`, { params: httpParams });
  }

  // GET /api/companies/{id} - Get company by ID
  getCompanyById(id: number): Observable<CompanyResponse> {
    return this.http.get<CompanyResponse>(`${this.apiUrl}/companies/${id}`);
  }

  // POST /api/companies - Create new company (requires Sales Rep or higher)
  createCompany(company: CompanyRequest): Observable<CompanyResponse> {
    return this.http.post<CompanyResponse>(`${this.apiUrl}/companies`, company);
  }

  // PUT /api/companies/{id} - Update existing company (requires Sales Rep or higher)
  updateCompany(id: number, company: CompanyRequest): Observable<CompanyResponse> {
    return this.http.put<CompanyResponse>(`${this.apiUrl}/companies/${id}`, company);
  }

  // DELETE /api/companies/{id} - Delete company (requires Admin or higher)
  deleteCompany(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/companies/${id}`);
  }

  // PATCH /api/companies/{id}/change-owner - Change company owner (requires Manager or higher)
  changeCompanyOwner(id: number, userPropietyId: number): Observable<CompanyResponse> {
    return this.http.patch<CompanyResponse>(`${this.apiUrl}/companies/${id}/change-owner`, { userPropietyId });
  }

  // GET /api/companies/export — descarga CSV con todas las empresas del business (Admin+)
  exportCompaniesCsv(): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/companies/export`, { responseType: 'blob' });
  }

  // GET /api/companies/import/template — descarga CSV vacío con el header (Admin+)
  downloadCompaniesImportTemplate(): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/companies/import/template`, { responseType: 'blob' });
  }

  // POST /api/companies/import?mode=... — sube CSV y procesa (Admin+)
  importCompaniesCsv(file: File, mode: ImportMode = 'SKIP'): Observable<ImportResultResponse> {
    const form = new FormData();
    form.append('file', file);
    const params = new HttpParams().set('mode', mode);
    return this.http.post<ImportResultResponse>(`${this.apiUrl}/companies/import`, form, { params });
  }
}
